import {
  ArcElement,
  Chart as ChartJS,
  Legend as ChartLegend,
  ChartOptions,
  Tooltip as ChartTooltip
} from 'chart.js'
import React, { useEffect, useMemo } from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { getOrgTimeRange, timeMetric } from '../../../utils/DateTimeUtils'

import { DDSTypography } from 'den-design-system'
import FullstackIcon from '../../../assets/icons/fullstack.svg'
import { MdOpenInNew } from 'react-icons/md'
import MulesoftIcon from '../../../assets/icons/newMulesoft.svg'
import { ProjectType } from '../../../enum/ProjectType.enum'
import { Chart as ReactChart } from 'react-chartjs-2'
import SalesforceIcon from '../../../assets/icons/newSalesforce.svg'
import { Settings } from '@carbon/icons-react'
import Tooltip from '../../../components/New/Tooltip/Tooltip'
import { observer } from 'mobx-react-lite'
import pallete from '../../../global/newPallete'
import { replaceUrl } from '../../../utils/UrlUtils'
import routeConstants from '../../../constants/RouteConstants'
import { useStoreContext } from '../../../store/StoreContext'

ChartJS.register(ArcElement, ChartTooltip, ChartLegend)

interface Breakdown {
  code: string
  count: number
}

interface SeriesData {
  name: string
  value: number
  color: string
  details?: any
  breakdown?: Breakdown[]
}

interface ApiMonitoringCardProps {
  projectId: string
  title: string
  totalApis: number
  failedApis: number
  mostFailedApi: {
    urlTemplate: string
    count: number
  } | null
  slowestResponse: {
    urlTemplate: string
    responseTimeInMilliseconds: number
  } | null
  donutData: SeriesData[]
  projectType: ProjectType
}

const ApiMonitoringCard: React.FC<ApiMonitoringCardProps> = ({
  title,
  totalApis,
  failedApis,
  mostFailedApi,
  slowestResponse,
  donutData,
  projectType,
  projectId
}) => {
  const navigate = useNavigate()
  const store = useStoreContext()

  useEffect(() => {
    return () => {
      const tooltipEl = document.getElementById('chartjs-tooltip')
      if (tooltipEl) {
        tooltipEl.remove()
      }
    }
  }, [])

  // Enhanced chart data processing for better visibility of small segments
  const enhancedChartData = useMemo(() => {
    if (donutData.every((d) => d.value === 0)) {
      return {
        labels: ['No Data'],
        datasets: [
          {
            data: [1],
            backgroundColor: ['#D3D3D3'],
            borderWidth: 0,
            hoverBorderWidth: 2,
            hoverBorderColor: '#ffffff'
          }
        ],
        originalData: donutData
      }
    }

    // Calculate total and find segments that are too small
    const total = donutData.reduce((sum, item) => sum + item.value, 0)
    const minVisiblePercentage = 0.02 // 2% minimum visibility
    const minVisibleValue = total * minVisiblePercentage

    // Define enhanced data type
    interface EnhancedSeriesData extends SeriesData {
      enhancedValue: number
      originalValue: number
    }

    // Create enhanced data with minimum arc sizes
    const enhancedData: EnhancedSeriesData[] = donutData.map((item) => {
      if (item.value === 0) {
        return {
          ...item,
          enhancedValue: item.value,
          originalValue: item.value
        }
      }

      // If the segment is too small, give it a minimum size for visibility
      const enhancedValue =
        item.value < minVisibleValue ? minVisibleValue : item.value

      return {
        ...item,
        enhancedValue,
        originalValue: item.value
      }
    })

    // Adjust the largest segment to compensate for artificial increases
    const largestSegmentIndex = enhancedData.findIndex(
      (item) =>
        item.originalValue ===
        Math.max(...enhancedData.map((d) => d.originalValue))
    )

    if (largestSegmentIndex !== -1) {
      const totalArtificialIncrease = enhancedData
        .filter(
          (item) =>
            item.originalValue < minVisibleValue && item.originalValue > 0
        )
        .reduce((sum, item) => sum + (minVisibleValue - item.originalValue), 0)

      const largestSegment = enhancedData[largestSegmentIndex]
      largestSegment.enhancedValue = Math.max(
        0,
        largestSegment.originalValue - totalArtificialIncrease
      )
    }

    return {
      labels: enhancedData.map((d) => d.name),
      datasets: [
        {
          data: enhancedData.map((d) => d.enhancedValue),
          backgroundColor: enhancedData.map((d) => d.color),
          borderWidth: 0,
          hoverBorderWidth: 2,
          hoverBorderColor: '#ffffff'
        }
      ],
      originalData: donutData
    }
  }, [donutData])

  const routeProjectDetails = () => {
    const orgId = store.scopeStore.getScope()['organization-id'] as string
    const envId = store.scopeStore.getScope()['environment-id'] as string

    const route = replaceUrl(routeConstants.VIEW_PROJECT, orgId, envId).replace(
      ':projectId',
      projectId as string
    )

    navigate({
      pathname: route,
      search: `?${createSearchParams({ tab: '1' })}`
    })
  }

  const routeAPIMonitoringDetails = (projectId: string) => {
    const orgId = store.scopeStore.getScope()['organization-id'] as string
    const envId = store.scopeStore.getScope()['environment-id'] as string

    const route = replaceUrl(
      routeConstants.API_MONITORING,
      orgId,
      projectId,
      envId
    )

    const orgTimeZone = store.userStore.getOrganizationPreference()?.timeZone
    const { startOfMonth, endOfCurrentDayTimestamp } =
      getOrgTimeRange(orgTimeZone)

    const queryParams = createSearchParams({
      page: '1',
      tab: '1',
      'created-from': String(startOfMonth),
      'created-to': String(endOfCurrentDayTimestamp),
      'time-zone': orgTimeZone
    }).toString()

    navigate(`${route}?${queryParams}`, { replace: true })
  }

  const externalTooltipHandler = (context: any) => {
    const tooltipModel = context.tooltip

    let tooltipEl = document.getElementById('chartjs-tooltip')
    if (!tooltipEl) {
      tooltipEl = document.createElement('div')
      tooltipEl.id = 'chartjs-tooltip'
      tooltipEl.style.cssText = `
      position: absolute;
      z-index: 9999;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s ease;
      font-family: inherit;
    `
      document.body.appendChild(tooltipEl)
    }

    if (tooltipModel.opacity === 0 || !tooltipModel.dataPoints?.length) {
      tooltipEl.style.opacity = '0'
      return
    }

    // Get the hovered arc
    const dataPoint = tooltipModel.dataPoints[0]
    const index = dataPoint.dataIndex
    const entry = enhancedChartData.originalData[index]
    if (!entry || entry.value === 0) {
      tooltipEl.style.opacity = '0'
      return
    }

    const details = entry.details || {}
    const total = details.total ?? entry.value

    // Tooltip HTML
    let innerHtml = `
    <div style="
      background: white;
      border: 1px solid ${pallete.colors.stroke2};
      border-radius: 8px;
      padding: 12px 16px;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
      min-width: 160px;
      font-size: 14px;
      line-height: 1.4;
      position: relative;
    ">
      <div style="
        text-align: center;
        font-weight: 600;
        color: ${pallete.colors.textDark4};
        margin-bottom: 8px;
        font-size: 15px;
      ">${entry.name}</div>
      <div style="
        display: flex;
        justify-content: space-between;
        margin-bottom: 6px;
        color: ${pallete.colors.textDark4};
        font-weight: 500;
      ">
        <span>Total:</span>
        <span style="font-weight: 600;">${total}</span>
      </div>
  `

    if (entry.details && typeof entry.details === 'object') {
      Object.entries(entry.details)
        .filter(([code]) => code !== 'total')
        .forEach(([code, count]) => {
          innerHtml += `
          <div style="
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
            color: ${pallete.colors.textDark4};
            font-size: 13px;
          ">
            <span>${code}:</span>
            <span style="font-weight: 500;">${count}</span>
          </div>
        `
        })
    }

    // Chevron pointing down
    innerHtml += `
    <div style="
      position: absolute;
      left: 50%;
      bottom: -6px;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid white;
      filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.15));
    "></div>
  `

    innerHtml += `</div>`
    tooltipEl.innerHTML = innerHtml

    // Position tooltip exactly at caret
    const chartRect = context.chart.canvas.getBoundingClientRect()
    const caretX = chartRect.left + tooltipModel.caretX + window.scrollX
    const caretY = chartRect.top + tooltipModel.caretY + window.scrollY

    const tooltipWidth = tooltipEl.offsetWidth || 160
    const tooltipHeight = tooltipEl.offsetHeight || 100

    let left = caretX - tooltipWidth / 2
    let top = caretY - tooltipHeight - 12 // above arc

    if (left < 0) left = 8
    if (left + tooltipWidth > window.innerWidth)
      left = window.innerWidth - tooltipWidth - 8
    if (top < 0) top = caretY + 12 // below if not enough space

    tooltipEl.style.left = left + 'px'
    tooltipEl.style.top = top + 'px'
    tooltipEl.style.opacity = '1'
  }

  const chartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '75%',
    interaction: {
      intersect: true,
      mode: 'nearest'
    },
    onHover: (event, elements) => {
      // Change cursor on hover
      const canvas = event.native?.target as HTMLCanvasElement
      if (canvas) {
        canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default'
      }
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: externalTooltipHandler
      },
      legend: {
        display: false
      }
    },
    elements: {
      arc: {
        borderWidth: 0,
        hoverBorderWidth: 2,
        hoverBorderColor: '#ffffff'
      }
    }
  }

  return (
    <div
      className='bg-white rounded-[10px] border overflow-visible'
      style={{ borderColor: pallete.colors.stroke2 }}
    >
      <div className='flex items-center justify-between p-[12px]'>
        <div className='flex items-center gap-[8px]'>
          <DDSTypography.Title
            type='h5'
            variant='bold'
            color={pallete.colors.textDark3}
          >
            {title}
          </DDSTypography.Title>
          <img
            src={
              projectType === 'FULL_STACK_ONLY'
                ? FullstackIcon
                : projectType === 'SALESFORCE_ONLY'
                  ? SalesforceIcon
                  : MulesoftIcon
            }
            sizes='16px'
            width='16px'
            height='16px'
          />
        </div>
        <div className='flex items-center gap-[4px] '>
          <button
            className='p-[8px] hover:bg-gray-100 rounded-lg transition-colors'
            onClick={routeProjectDetails}
          >
            <Settings color={pallete.colors.textDark3} size={18} />
          </button>
          <button
            className='p-[8px] hover:bg-gray-100 rounded-lg transition-colors'
            onClick={() => routeAPIMonitoringDetails(projectId)}
          >
            <MdOpenInNew color={pallete.colors.textDark3} size={18} />
          </button>
        </div>
      </div>
      <hr style={{ borderColor: pallete.colors.stroke2 }} />

      <div className='grid grid-cols-3 gap-[16px] p-[16px]'>
        <div className='flex flex-col gap-[16px]'>
          <div>
            <DDSTypography.Paragraph
              size='para'
              variant='semiBold'
              color={pallete.colors.textDark6}
            >
              {`Failed API`}
            </DDSTypography.Paragraph>
            <DDSTypography.Title
              type='h5'
              variant='semiBold'
              color={pallete.colors.black}
            >
              {failedApis}
            </DDSTypography.Title>
          </div>
          <div>
            <DDSTypography.Paragraph
              size='para'
              variant='semiBold'
              color={pallete.colors.textDark6}
            >
              {`Most Failed API`}
            </DDSTypography.Paragraph>

            {mostFailedApi ? (
              <>
                <Tooltip
                  id={`tooltip-url-${mostFailedApi.urlTemplate}`}
                  label={mostFailedApi.urlTemplate}
                  className={`override-tooltip-arrow max-w-[90%] break-all ${
                    typeof mostFailedApi.urlTemplate === 'string' &&
                    mostFailedApi.urlTemplate.length > 22
                      ? ''
                      : '!hidden'
                  }`}
                  position='right'
                >
                  <div>
                    <DDSTypography.Title
                      type='h5'
                      variant='semiBold'
                      color={pallete.colors.black}
                      className='tooltip-container sm:block overflow-hidden truncate max-w-[186px]'
                    >
                      {mostFailedApi.urlTemplate}
                    </DDSTypography.Title>
                  </div>
                </Tooltip>

                <div>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.textDark4}
                    className='flex items-center space-x-1'
                  >
                    <span style={{ color: pallete.colors.textDark5 }}>
                      Calls:
                    </span>
                    <span> {mostFailedApi.count ?? '-'}</span>
                  </DDSTypography.Paragraph>
                </div>
              </>
            ) : (
              <div>-</div>
            )}
          </div>
          <div>
            <DDSTypography.Paragraph
              size='para'
              variant='semiBold'
              color={pallete.colors.textDark6}
            >
              Slowest Response Time
            </DDSTypography.Paragraph>

            {slowestResponse ? (
              <>
                <Tooltip
                  id={`tooltip-url-${slowestResponse?.urlTemplate}`}
                  label={slowestResponse?.urlTemplate}
                  className={`override-tooltip-arrow max-w-[90%] break-all ${
                    typeof slowestResponse?.urlTemplate === 'string' &&
                    slowestResponse?.urlTemplate.length > 22
                      ? ''
                      : '!hidden'
                  }`}
                  position='right'
                >
                  {' '}
                  <div>
                    <DDSTypography.Title
                      type='h5'
                      variant='semiBold'
                      className='tooltip-container sm:block overflow-hidden truncate max-w-[186px]'
                    >
                      {slowestResponse?.urlTemplate}
                    </DDSTypography.Title>
                  </div>
                </Tooltip>

                <div>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='regular'
                    color={pallete.colors.textDark4}
                    className='flex items-center space-x-1'
                  >
                    <span style={{ color: pallete.colors.textDark5 }}>
                      Duration:
                    </span>
                    <span>
                      {timeMetric(
                        slowestResponse?.responseTimeInMilliseconds,
                        'ms'
                      )}
                    </span>
                  </DDSTypography.Paragraph>
                </div>
              </>
            ) : (
              <div>-</div>
            )}
          </div>
        </div>

        {/* Middle Section - Chart */}
        <div className='flex justify-center items-center'>
          <div className='relative h-[220px] w-[220px] left-[28px]'>
            <ReactChart
              type='doughnut'
              data={enhancedChartData}
              options={chartOptions}
            />
            <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
              <div className='text-center'>
                <DDSTypography.Paragraph
                  size='para'
                  variant='semiBold'
                  color={pallete.colors.textDark4}
                >
                  Total APIs
                </DDSTypography.Paragraph>
                <DDSTypography.Title
                  type='h5'
                  variant='semiBold'
                  color={pallete.colors.black}
                >
                  {totalApis}
                </DDSTypography.Title>
              </div>
            </div>
          </div>
        </div>

        <div className='flex flex-col justify-center items-end gap-[8px]'>
          {donutData
            .filter((item) => item.value > 0)
            .map((item) => (
              <div key={item.name} className='flex items-center gap-[8px]'>
                <div
                  className='w-[8px] h-[8px] min-w-[8px] min-h-[8px] rounded-full shrink-0'
                  style={{ backgroundColor: item.color }}
                />

                <DDSTypography.Paragraph
                  size='para'
                  variant='regular'
                  color={pallete.colors.textDark5}
                >
                  {item.name}
                </DDSTypography.Paragraph>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default observer(ApiMonitoringCard)
